'use client';

/**
 * FormCurveCard — visualizes the Performance Management Chart
 * (Fitness/Fatigue/Form) on the Training tab.
 *
 * Layout:
 *   - Headline: current Form (TSB) with zone label and 7-day delta
 *   - Three KPI tiles: Fitness (CTL), Fatigue (ATL), Form (TSB)
 *   - SVG chart: 84 days of TSB area + CTL/ATL lines overlay, with
 *     horizontal zone bands (risk / overreaching / productive /
 *     maintenance / fresh) so the user sees at a glance which
 *     regime they've been training in
 *   - Plain-English interpretation line tied to the current zone
 *
 * Loading model: caller passes the FormCurveSummary directly. The
 * card renders an "insufficient data" placeholder when the summary
 * is null (< 7 sessions). This keeps the component pure — no async,
 * no hooks beyond useState for the optional hover tooltip.
 *
 * Wearable-less parity: this card requires nothing from the
 * wearable. Pure session-load math.
 */

import { useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import OpsPanel from './OpsPanel';
import type { FormCurveSummary, FormZone } from '@/lib/formCurve';

interface Props {
  summary: FormCurveSummary | null;
}

// ZONE_LABEL removed with the verdict callout — see the note on the panel
// below. The zone still drives the colour via ZONE_TW; it just no longer
// spells out an instruction this model's units cannot support.

const ZONE_TW: Record<FormZone, string> = {
  'fresh-detrained': 'text-cyan-400',
  'fresh-peaking':   'text-emerald-400',
  'maintenance':     'text-emerald-400',
  'productive':      'text-amber-300',
  'building':        'text-cyan-400',
  'overreaching':    'text-amber-400',
  'risk':            'text-red-400',
};

export default function FormCurveCard({ summary }: Props) {
  if (!summary) {
    return (
      <OpsPanel badge="Form Curve" code="SYS·PMC·01" accent="cyan"
        callout={<span className="text-slate-500">LEARNING</span>}>
        <p className="text-[12px] text-slate-400 leading-snug">
          Log 7+ sessions to unlock the fitness/fatigue/form model. The curve tracks chronic training load (CTL), acute load (ATL), and the gap between them (Form = CTL − ATL) — the gold standard for "am I fresh, productive, or overreaching."
        </p>
      </OpsPanel>
    );
  }

  const tsbTw = ZONE_TW[summary.zone];
  const tsbDeltaTw = summary.tsbDelta7d > 0 ? 'text-cyan-400'
    : summary.tsbDelta7d < 0 ? 'text-amber-400' : 'text-slate-400';

  // Raw CTL/ATL/TSB are tonnage-scaled (thousands) and meaningless to a
  // user. Present a readable INDEX instead: fitness/fatigue as a % of the
  // window's peak chronic load (100 = your best recent fitness), and form
  // as a % of current fitness — which is exactly the ratio classifyForm
  // uses, so the number tracks the zone label. The chart below is already
  // scale-invariant (zone bands are fractions of CTL), so it's untouched.
  // Peak is taken over the PRIOR points, excluding today's. Including today
  // meant that during any build the current value WAS the peak, so "Fitness"
  // read exactly 100 and could only ever fall — a headline KPI structurally
  // incapable of showing progress. Excluding it lets a genuine new best read
  // above 100, which is the thing the user actually wants to see.
  const priorPoints = summary.points.length > 1 ? summary.points.slice(0, -1) : summary.points;
  const peak = Math.max(1, ...priorPoints.map(p => Math.max(p.ctl, p.atl)));
  const fitnessIdx = Math.round((summary.ctl / peak) * 100);
  const fatigueIdx = Math.round((summary.atl / peak) * 100);
  const formPct = summary.ctl > 0 ? Math.round((summary.tsb / summary.ctl) * 100) : 0;
  const fitnessDelta7d = (summary.ctlDelta7d / peak) * 100;
  const formDelta7dPct = summary.ctl > 0 ? (summary.tsbDelta7d / summary.ctl) * 100 : 0;

  return (
    /* The zone VERDICT ("OVERREACH RISK", "DE-TRAINING") no longer renders.
     *
     * Two fatigue models sat on this tab and both printed an imperative: this
     * one and ACWR, ~200px apart, on different units, different windows and
     * different inputs. They can contradict each other, and nothing told the
     * user they were separate systems.
     *
     * ACWR keeps the verdict because it is dimensionally consistent — session
     * RPE x duration, in minutes, for every modality. This model sums a
     * mosaic of invented per-modality constants (strength = raw tonnage,
     * cardio = max(min*5, sec*50), circuit = rounds*5000, bodyweight =
     * reps*100), under which a 30-minute jog scores about 2.6x a hard 20,000 lb
     * lifting session. A uniform scale error would be invisible here because
     * the chart normalises to its own peak — but a change in modality MIX is
     * not uniform, so stopping cardio for one week while lifting identically
     * flips this to "DE-TRAINING" for someone who reduced nothing.
     *
     * The trend line is still useful as context, so the chart and the indices
     * stay. It just no longer tells the user what to do. */
    <OpsPanel badge="Form Curve" code="SYS·PMC·01" accent="cyan"
      callout={
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Trend · 42d
        </span>
      }>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <KpiTile
          label="Fitness"
          sublabel="Index · 42d"
          value={`${fitnessIdx}`}
          delta={fitnessDelta7d}
          deltaUnit=""
          valueTw="text-emerald-300"
        />
        <KpiTile
          label="Fatigue"
          sublabel="Index · 7d"
          value={`${fatigueIdx}`}
          valueTw="text-amber-300"
        />
        <KpiTile
          label="Form"
          sublabel="% of fitness"
          value={`${formPct > 0 ? '+' : ''}${formPct}%`}
          delta={formDelta7dPct}
          deltaUnit="%"
          valueTw={tsbTw}
        />
      </div>

      <FormCurveChart points={summary.points} ctl={summary.ctl} />

      {/* summary.interpretation is NOT rendered.
          Removing the zone callout was not enough — this line carried the same
          verdict in prose ("Very fresh — likely de-training. Time to ramp load
          back up.") and was caught rendering directly above ACWR's "You're in
          the sweet spot. Safe to keep pushing." on the same screen, about
          400px apart. That is the exact contradiction this card was supposed
          to stop printing.
          What replaces it says what the chart IS rather than what to do. */}
      <p className="text-[11px] text-slate-500 leading-snug mt-3">
        Fitness vs fatigue over 42 days. Read the shape, not the number — load
        units differ by training type, so compare this against your own trend
        rather than a target. Training Load Balance below is the deload call.
      </p>
      <div className="mt-2 pt-2 border-t border-slate-800/60 flex items-center justify-between gap-2 font-mono text-[9px] text-slate-600 uppercase tracking-[0.18em] tabular-nums">
        <span>
          7d Form delta · <span className={tsbDeltaTw}>
            {formDelta7dPct > 0 ? '+' : ''}{formDelta7dPct.toFixed(0)}%
          </span>
        </span>
        <span>{summary.sessionsAnalyzed} sessions · {summary.daysAnalyzed} days</span>
      </div>
    </OpsPanel>
  );
}

function KpiTile({ label, sublabel, value, delta, deltaUnit, valueTw }: {
  label: string; sublabel: string; value: string;
  delta?: number; deltaUnit?: string; valueTw: string;
}) {
  const showDelta = typeof delta === 'number' && Math.abs(delta) >= 0.5;
  const DeltaIcon = !showDelta ? Minus : delta! > 0 ? TrendingUp : TrendingDown;
  const deltaTw = !showDelta ? 'text-slate-600'
    : delta! > 0 ? 'text-emerald-400' : 'text-amber-400';
  return (
    <div className="rounded-md border border-slate-800/80 bg-slate-900/40 px-2 py-2">
      <div className="font-mono text-[9px] text-slate-500 uppercase tracking-[0.18em]">{label}</div>
      <div className="font-mono text-[8px] text-slate-700 uppercase tracking-[0.16em]">{sublabel}</div>
      <div className={`font-heading text-2xl font-bold tabular-nums leading-none mt-1 ${valueTw}`}>{value}</div>
      {typeof delta === 'number' && (
        <div className={`flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.14em] tabular-nums mt-1 ${deltaTw}`}>
          <DeltaIcon className="w-2.5 h-2.5" />
          <span>{showDelta ? `${delta! > 0 ? '+' : ''}${delta!.toFixed(0)}${deltaUnit ?? ''} 7d` : 'flat'}</span>
        </div>
      )}
    </div>
  );
}

/**
 * SVG chart: TSB area + CTL/ATL line overlays + horizontal zone bands.
 * Hover surfaces date + values via a vertical guide line.
 */
function FormCurveChart({ points, ctl }: { points: FormCurveSummary['points']; ctl: number }) {
  const [hover, setHover] = useState<number | null>(null);

  const { tsbMin, tsbMax, ctlMax } = useMemo(() => {
    let tsbMin = -15, tsbMax = 15, ctlMax = 10;
    for (const p of points) {
      if (p.tsb < tsbMin) tsbMin = p.tsb;
      if (p.tsb > tsbMax) tsbMax = p.tsb;
      if (p.ctl > ctlMax) ctlMax = p.ctl;
      if (p.atl > ctlMax) ctlMax = p.atl;
    }
    // Pad so the highest/lowest values aren't clipped against the frame
    return { tsbMin: tsbMin - 2, tsbMax: tsbMax + 2, ctlMax: ctlMax * 1.1 };
  }, [points]);

  // Peak chronic load over the window — the 100 reference for the tooltip
  // index (matches the KPI tiles above).
  const peak = useMemo(() => Math.max(1, ...points.map(p => Math.max(p.ctl, p.atl))), [points]);

  const W = 320;
  const H = 120;
  const PAD_L = 20;
  const PAD_R = 8;
  const PAD_T = 8;
  const PAD_B = 16;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  // Two y-scales:
  //   Left axis: TSB (-N..+N) drawn as the filled area in the background
  //   Right axis: CTL/ATL (0..max) drawn as overlay lines
  // Both share the x scale.
  const xAt = (i: number) => PAD_L + (i / Math.max(1, points.length - 1)) * plotW;
  const yTsb = (v: number) => {
    const range = tsbMax - tsbMin;
    return PAD_T + (1 - (v - tsbMin) / range) * plotH;
  };
  const yCtl = (v: number) => {
    return PAD_T + (1 - v / ctlMax) * plotH;
  };

  // Zone band rectangles for the TSB axis. Boundaries are FRACTIONS of
  // current chronic fitness (CTL), matching classifyForm — so the bands
  // stay meaningful whether load units are TSS-scale or tonnage-scale
  // (here, thousands). A fixed ±5/±10 band would be an invisible sliver.
  const b = (frac: number) => ctl * frac;
  const tsbZones: Array<{ from: number; to: number; tw: string; key: string }> = [
    { from: b(0.05),  to: tsbMax,    tw: 'fill-cyan-500/8',    key: 'fresh' },
    { from: b(-0.08), to: b(0.05),   tw: 'fill-emerald-500/6', key: 'maintenance' },
    { from: b(-0.30), to: b(-0.08),  tw: 'fill-amber-500/6',   key: 'productive' },
    { from: b(-0.50), to: b(-0.30),  tw: 'fill-amber-500/12',  key: 'overreaching' },
    { from: tsbMin,   to: b(-0.50),  tw: 'fill-red-500/12',    key: 'risk' },
  ].filter(z => z.to > z.from);

  const tsbAreaPath = useMemo(() => {
    if (points.length === 0) return '';
    const zeroY = yTsb(0);
    let d = `M ${xAt(0)} ${zeroY}`;
    for (let i = 0; i < points.length; i++) {
      d += ` L ${xAt(i).toFixed(1)} ${yTsb(points[i].tsb).toFixed(1)}`;
    }
    d += ` L ${xAt(points.length - 1).toFixed(1)} ${zeroY} Z`;
    return d;
  }, [points, tsbMin, tsbMax]);

  const ctlPath = useMemo(() => {
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yCtl(p.ctl).toFixed(1)}`).join(' ');
  }, [points, ctlMax]);

  const atlPath = useMemo(() => {
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yCtl(p.atl).toFixed(1)}`).join(' ');
  }, [points, ctlMax]);

  const tsbZeroY = yTsb(0);

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        onMouseLeave={() => setHover(null)}
        onMouseMove={e => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * W;
          const idx = Math.round(((x - PAD_L) / plotW) * (points.length - 1));
          setHover(Math.max(0, Math.min(points.length - 1, idx)));
        }}
      >
        {/* Background zone bands for TSB */}
        {tsbZones.map(z => (
          <rect
            key={z.key}
            x={PAD_L}
            y={yTsb(z.to)}
            width={plotW}
            height={Math.max(0, yTsb(z.from) - yTsb(z.to))}
            className={z.tw}
          />
        ))}

        {/* Zero line */}
        <line
          x1={PAD_L} x2={W - PAD_R}
          y1={tsbZeroY} y2={tsbZeroY}
          className="stroke-slate-700"
          strokeWidth="0.5"
          strokeDasharray="2 2"
        />

        {/* TSB filled area — the star of the show */}
        <path d={tsbAreaPath} className="fill-cyan-400/20" />
        {/* TSB line on top of the fill for definition */}
        <path
          d={points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yTsb(p.tsb).toFixed(1)}`).join(' ')}
          className="stroke-cyan-300 fill-none"
          strokeWidth="1"
        />

        {/* CTL line — emerald */}
        <path d={ctlPath} className="stroke-emerald-400 fill-none" strokeWidth="1" strokeDasharray="3 2" />
        {/* ATL line — amber */}
        <path d={atlPath} className="stroke-amber-400 fill-none" strokeWidth="1" />

        {/* Hover guide + last-point marker */}
        {hover !== null && points[hover] && (
          <>
            <line
              x1={xAt(hover)} x2={xAt(hover)}
              y1={PAD_T} y2={H - PAD_B}
              className="stroke-amber-400/40"
              strokeWidth="0.5"
            />
            <circle cx={xAt(hover)} cy={yTsb(points[hover].tsb)} r="2" className="fill-cyan-300" />
          </>
        )}

        {/* TSB axis markers — direction, not absolute load units */}
        <text x="2" y={PAD_T + 6} className="fill-slate-600 font-mono" fontSize="6">FRESH</text>
        <text x="2" y={yTsb(0) + 3} className="fill-slate-600 font-mono" fontSize="7">0</text>
        <text x="2" y={H - PAD_B - 1} className="fill-slate-600 font-mono" fontSize="6">FATIGUE</text>

        {/* X-axis end markers */}
        <text x={PAD_L} y={H - 3} className="fill-slate-600 font-mono" fontSize="7" textAnchor="start">
          {formatChartDate(points[0]?.date)}
        </text>
        <text x={W - PAD_R} y={H - 3} className="fill-slate-600 font-mono" fontSize="7" textAnchor="end">
          today
        </text>
      </svg>

      {/* Hover tooltip */}
      {hover !== null && points[hover] && (
        <div className="absolute top-1 right-1 bg-slate-900/95 border border-slate-700 rounded px-2 py-1 font-mono text-[9px] uppercase tracking-[0.14em] tabular-nums">
          <div className="text-slate-400">{formatChartDate(points[hover].date)}</div>
          <div className="text-emerald-300">Fitness {Math.round((points[hover].ctl / peak) * 100)}</div>
          <div className="text-amber-300">Fatigue {Math.round((points[hover].atl / peak) * 100)}</div>
          <div className="text-cyan-300">Form {points[hover].ctl > 0 ? `${points[hover].tsb > 0 ? '+' : ''}${Math.round((points[hover].tsb / points[hover].ctl) * 100)}%` : '—'}</div>
        </div>
      )}

      {/* Legend row */}
      <div className="flex items-center justify-center gap-4 mt-1 font-mono text-[9px] uppercase tracking-[0.14em]">
        <LegendDot tw="bg-cyan-400" label="Form (TSB)" />
        <LegendDot tw="bg-emerald-400" label="Fitness (CTL)" dashed />
        <LegendDot tw="bg-amber-400" label="Fatigue (ATL)" />
      </div>
    </div>
  );
}

function LegendDot({ tw, label, dashed }: { tw: string; label: string; dashed?: boolean }) {
  return (
    <span className="flex items-center gap-1.5 text-slate-500">
      <span className={`w-3 h-px ${tw} ${dashed ? 'opacity-70' : ''}`} aria-hidden />
      <span>{label}</span>
    </span>
  );
}

function formatChartDate(iso?: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${m}/${d}`;
}
